import { fromJS, List } from 'immutable';
import getIn from 'lodash/get';

import { ApiActions } from '../../constants/ApiActions';
import { ActionTypes, PlaylistConstants } from '../../constants/PlaylistT3';
import { makeSparseArray } from '../../lib/ArrayUtils';
import { getNextOptimisticVersion, willClobberOptimisticTrackEdits } from '../../lib/EntityUtils';
import { Map } from '../../lib/ImmutableUtils';
import { getNewTracksFromEdits, getValidInserts } from '../../lib/MusicUtils';
import { createReducer } from '../../lib/ReducerUtils';
import Logger from '../../tools/Logger';

const log = Logger.child({ component: 'reducers/entities/Playlists' });

const INITIAL_STATE = Map<$TSFixMe>({});
export type EntitiesPlaylistsState = typeof INITIAL_STATE;

// T3 PLAYLIST ENTITY REDUCER
export default createReducer(INITIAL_STATE, {
  [ActionTypes.INTENT_ADD_TRACK_FOR_FEEDBACK]: optimisticAddTrackForFeedback,
  [ActionTypes.INTENT_DELETE_TRACK_FOR_FEEDBACK]: optimisticDeleteTrackForFeedback,
  [ApiActions.PLAYLISTS_CREATE_SUCCESS]: savePlaylistData,
  [ActionTypes.MAKE_OPTIMISTIC_EDIT]: onOptimisticEdit,
  [ActionTypes.ROLLBACK_OPTIMISTIC_EDIT]: onOptimisticEditRollback,
  [ApiActions.PLAYLISTS_ANNOTATE_PLAYLISTS_SUCCESS]: savePlaylistData,
  [ApiActions.PLAYLISTS_GET_TRACKS_SUCCESS]: savePlaylistTrackData,
  [ActionTypes.CLEAR_PLAYLIST_TRACKLIST]: clearTracklist,
  [ApiActions.PLAYLISTS_EDIT_TRACKS_SUCCESS]: savePlaylistEditTrackData,
  [ApiActions.PLAYLISTS_APPEND_ITEMS_SUCCESS]: unimplemented,
  [ApiActions.PODS_GET_PLAYLIST_AUTOFILL_SONGS_SUCCESS]: savePlaylistData,
  [ApiActions.PODS_GET_PLAYLIST_SEARCH_RECOMMENDATIONS_SUCCESS]: savePlaylistRecommendationsData,
  [ApiActions.PLAYLISTS_DELETE_TRACKS_SUCCESS]: unimplemented,
  [ActionTypes.MAKE_OPTIMISTIC_DELETE]: onOptimisticDelete,
  [ApiActions.PLAYLISTS_SET_DETAILS_SUCCESS]: savePlaylistData,
  [ActionTypes.DELETE_OPTIMISTIC_PLAYLIST_DATA]: deleteOptimisticPlaylistData,
  [ActionTypes.UPDATE_OPTIMISTIC_GET_TRACKS_RETRY]: updateOptimisticGetTracksRetry,
});

// CASE FUNCTIONS

function unimplemented(state: EntitiesPlaylistsState): EntitiesPlaylistsState {
  return state;
}

function onOptimisticEdit(state: EntitiesPlaylistsState, action: $TSFixMe): EntitiesPlaylistsState {
  const { moves, inserts, id } = action.data;
  const tracks = state.getIn([id, 'tracks']);
  const previousVersion = state.getIn([id, 'version']);
  if (tracks) {
    if (moves) {
      const newTracks = getNewTracksFromEdits(moves, tracks);
      return state
        .setIn([id, 'tracks'], newTracks)
        .setIn([id, 'optimisticVersion'], previousVersion + 1);
    } else if (inserts) {
      const previousTotal = state.getIn([id, 'totalTracks']);
      const lastTrackItemId = state.getIn([id, 'tracks', -1, 'itemId'], previousTotal);
      const validInserts = getValidInserts(inserts, tracks);
      const tracksWithInserts = tracks.concat(
        // @ts-expect-error
        validInserts.map((insert, i) =>
          Map({
            trackPandoraId: insert.trackPandoraId,
            itemId: lastTrackItemId + i + 1,
          }),
        ),
      );
      return state
        .setIn([id, 'tracks'], tracksWithInserts)
        .setIn([id, 'optimisticVersion'], previousVersion + 1)
        .setIn([id, 'totalTracks'], previousTotal + 1);
    }
  }
  return state;
}

function onOptimisticEditRollback(
  state: EntitiesPlaylistsState,
  action: $TSFixMe,
): EntitiesPlaylistsState {
  const { previousTracks, previousTotalTracks, id } = action.data;
  if (previousTracks && id && previousTotalTracks >= 0) {
    return state
      .setIn([id, 'tracks'], previousTracks)
      .setIn([id, 'optimisticVersion'], null)
      .setIn([id, 'totalTracks'], previousTotalTracks);
  }
  return state;
}

function optimisticAddTrackForFeedback(
  state: EntitiesPlaylistsState,
  action: $TSFixMe,
): EntitiesPlaylistsState {
  let resultState = state;
  // optimistically add track to beginning of playlist
  const { playlistId, trackId } = action.data;
  log.trace('optimistic add track: ' + trackId + ' to playlist: ' + playlistId);
  const tracks = state.getIn([playlistId, 'tracks'], null);
  const version = state.getIn([playlistId, 'version'], null);
  if (tracks) {
    const optimisticVersion = getNextOptimisticVersion(state, playlistId);
    const newTracks = tracks.unshift(
      Map({
        itemId: version,
        trackPandoraId: trackId,
        addedTimestamp: null,
      }),
    );
    const previousTotalTracks = state.getIn([playlistId, 'totalTracks'], 0);
    resultState = state
      .setIn([playlistId, 'tracks'], newTracks)
      .setIn([playlistId, 'optimisticVersion'], optimisticVersion)
      .setIn([playlistId, 'totalTracks'], previousTotalTracks + 1);
  }
  return resultState;
}

function doDeletePlaylistTrack(
  state: EntitiesPlaylistsState,
  playlistId: string | $TSFixMe,
  trackItemId: string | $TSFixMe,
): EntitiesPlaylistsState {
  let resultState = state;
  const tracks = state.getIn([playlistId, 'tracks'], null);
  if (tracks) {
    const optimisticVersion = getNextOptimisticVersion(state, playlistId);
    // @ts-expect-error
    const itemIndex = tracks.findIndex(value => value.get('itemId') === trackItemId);
    if (itemIndex >= 0) {
      const newTracks = tracks.delete(itemIndex);
      const previousTotalTracks = state.getIn([playlistId, 'totalTracks'], 1);
      resultState = state
        .setIn([playlistId, 'tracks'], newTracks)
        .setIn([playlistId, 'optimisticVersion'], optimisticVersion)
        .setIn([playlistId, 'totalTracks'], previousTotalTracks - 1);
    }
  }
  return resultState;
}

function optimisticDeleteTrackForFeedback(
  state: EntitiesPlaylistsState,
  action: $TSFixMe,
): EntitiesPlaylistsState {
  const { playlistId, trackItemId } = action.data;
  log.trace('optimistic delete track: ' + trackItemId + ' to playlist: ' + playlistId);
  return doDeletePlaylistTrack(state, playlistId, trackItemId);
}

function onOptimisticDelete(
  state: EntitiesPlaylistsState,
  action: $TSFixMe,
): EntitiesPlaylistsState {
  const { pandoraId, trackItemIds } = action.data;
  const trackItemId = trackItemIds[0];
  return doDeletePlaylistTrack(state, pandoraId, trackItemId);
}

function savePlaylistData(state: EntitiesPlaylistsState, action: $TSFixMe): EntitiesPlaylistsState {
  return state.mergeDeep(action.response.entities.playlists);
}

function savePlaylistTrackData(
  state: EntitiesPlaylistsState,
  action: $TSFixMe,
): EntitiesPlaylistsState {
  return savePlaylistNoAnnotations(state, action, action.params.content.request.pandoraId);
}

function clearTracklist(state: EntitiesPlaylistsState, action: $TSFixMe): EntitiesPlaylistsState {
  return state.setIn([action.data.pandoraId, 'tracks'], List());
}

function savePlaylistRecommendationsData(
  state: EntitiesPlaylistsState,
  action: $TSFixMe,
): EntitiesPlaylistsState {
  // delete old recommendations before saving new ones
  const playlistId = action.params.content.playlistId;
  return savePlaylistNoAnnotations(
    state.removeIn([playlistId, 'songRecommendations']),
    action,
    playlistId,
  );
}

function savePlaylistNoAnnotations(
  state: EntitiesPlaylistsState,
  action: $TSFixMe,
  playlistId: string,
): EntitiesPlaylistsState {
  const newPlaylist = getIn(action, ['response', 'entities', 'playlists', playlistId], null);
  if (newPlaylist) {
    const oldVersion = state.getIn([playlistId, 'version'], null);
    const oldOptimisticVersion = state.getIn([playlistId, 'optimisticVersion'], null);
    const newVersion = newPlaylist.version;
    // separate out the annotations - they're stored elsewhere in the catalog entity
    let playlistNoAnnotations = fromJS(newPlaylist).delete('annotations');
    if (!willClobberOptimisticTrackEdits(oldVersion, oldOptimisticVersion, newVersion)) {
      const oldTracks = state.getIn([playlistId, 'tracks'], List());
      const offset = playlistNoAnnotations.get('offset');
      const totalTracks = playlistNoAnnotations.get('totalTracks');
      const sparseTracks = makeSparseArray(totalTracks, i => {
        const indexBeforeOffset = i - offset;
        return indexBeforeOffset < 0
          ? oldTracks.get(i)
          : playlistNoAnnotations.getIn(['tracks', indexBeforeOffset], oldTracks.get(i, Map()));
      });
      playlistNoAnnotations = playlistNoAnnotations.set('tracks', List(sparseTracks));
      // if current version caught up with optimistic edits
      // ..or optimisticVersion skipped ahead, clean out optimistic edit data
      if (
        oldOptimisticVersion &&
        (newVersion >= oldOptimisticVersion || oldOptimisticVersion > newVersion + 1)
      ) {
        // @ts-expect-error
        playlistNoAnnotations.withMutations(subState => {
          return subState.set('optimisticGetTracksRetryCount', 0).set('optimisticVersion', null);
        });
      }
      // @ts-expect-error
      return state.mergeDeep(Map({ [playlistId]: playlistNoAnnotations }));
    }
  }
  return state;
}

function savePlaylistEditTrackData(
  state: EntitiesPlaylistsState,
  action: $TSFixMe,
): EntitiesPlaylistsState {
  const { version, pandoraId } = action.response;
  if (version) {
    return state.setIn([pandoraId, 'version'], version);
  }
  // in this case we do nothing since the edit response is lacking tracklist & annotations
  // the worker will call getTracks to update the playlist data in the client
  return state;
}

function doDeleteOptimisticPlaylistData(
  state: EntitiesPlaylistsState,
  id: string,
): EntitiesPlaylistsState {
  return state.withMutations(subState => {
    return subState
      .removeIn([id, 'optimisticGetTracksRetryCount'])
      .removeIn([id, 'optimisticVersion']);
  });
}

function deleteOptimisticPlaylistData(
  state: EntitiesPlaylistsState,
  action: $TSFixMe,
): EntitiesPlaylistsState {
  const { id } = action.data;
  return doDeleteOptimisticPlaylistData(state, id);
}

function updateOptimisticGetTracksRetry(
  state: EntitiesPlaylistsState,
  action: $TSFixMe,
): EntitiesPlaylistsState {
  let resultState = state;
  const { playlistId } = action.data;
  const optimisticVersion = state.getIn([playlistId, 'optimisticVersion'], null);
  if (optimisticVersion) {
    const optimisticGetTracksRetryCount = state.getIn(
      [playlistId, 'optimisticGetTracksRetryCount'],
      0,
    );
    if (optimisticGetTracksRetryCount >= PlaylistConstants.OPTIMISTIC_GET_TRACKS_RETRY_MAX) {
      resultState = doDeleteOptimisticPlaylistData(state, playlistId);
    } else {
      resultState = state.mergeIn([playlistId], {
        optimisticGetTracksRetryCount: optimisticGetTracksRetryCount + 1,
      });
    }
  }
  return resultState;
}
